// src/graphql/searchQueries.js
import { gql } from '@apollo/client';

// Basic search query for studios
export const SEARCH_STUDIOS = gql`
  query SearchStudios(
    $minLat: Float!
    $maxLat: Float!
    $minLon: Float!
    $maxLon: Float!
  ) {
    listStudios(
      filter: {
        and: [
          { approxLatitude: { between: [$minLat, $maxLat] } }
          { approxLongitude: { between: [$minLon, $maxLon] } }
        ]
      }
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        User {
          id
          firstName
          lastName
        }
        tags {
          items {
            tag {
              id
              name
            }
          }
        }
        status
      }
      nextToken
    }
  }
`;

// Search query with group size filter
export const SEARCH_STUDIOS_WITH_GROUP_SIZE = gql`
  query SearchStudiosWithGroupSize(
    $minLat: Float!
    $maxLat: Float!
    $minLon: Float!
    $maxLon: Float!
    $numberOfPeople: Int!
  ) {
    listStudios(
      filter: {
        and: [
          { approxLatitude: { between: [$minLat, $maxLat] } }
          { approxLongitude: { between: [$minLon, $maxLon] } }
          { numberOfPeople: { ge: $numberOfPeople } }
        ]
      }
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        User {
          id
          firstName
          lastName
        }
        tags {
          items {
            tag {
              id
              name
            }
          }
        }
        status
      }
      nextToken
    }
  }
`;

// Query for searching tags by name (begins with)
export const SEARCH_TAGS_BEGIN = gql`
  query SearchTagsBegin($searchTerm: String!) {
    listTags(filter: { name: { beginsWith: $searchTerm } }) {
      items {
        id
        name
      }
    }
  }
`;

// Query for searching tags by name (contains)
export const SEARCH_TAGS_CONTAINS = gql`
  query SearchTagsContains($searchTerm: String!) {
    listTags(filter: { name: { contains: $searchTerm } }) {
      items {
        id
        name
      }
    }
  }
`;

// Search query optimized for search page with location
export const SEARCH_PAGE_STUDIOS_WITH_LOCATION = gql`
  query SearchPageStudiosWithLocation(
    $minLat: Float!
    $maxLat: Float!
    $minLon: Float!
    $maxLon: Float!
    $limit: Int
    $nextToken: String
  ) {
    listStudios(
      filter: {
        and: [
          { approxLatitude: { between: [$minLat, $maxLat] } }
          { approxLongitude: { between: [$minLon, $maxLon] } }
        ]
      }
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        studioTypes
        studioSize
        numberOfPeople
        city
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        tags {
          items {
            tag {
              id
              name
            }
          }
        }
        status
      }
      nextToken
    }
  }
`;

// Search query for all studios (no location filter)
export const SEARCH_PAGE_ALL_STUDIOS = gql`
  query SearchPageAllStudios(
    $limit: Int
    $nextToken: String
  ) {
    listStudios(
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        studioTypes
        studioSize
        numberOfPeople
        city
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        tags {
          items {
            tag {
              id
              name
            }
          }
        }
        status
      }
      nextToken
    }
  }
`;

// Search query for all studios with approximate public location fields
export const SEARCH_PAGE_ALL_STUDIOS_UNAUTH = gql`
  query SearchPageAllStudiosUnauth(
    $limit: Int
    $nextToken: String
  ) {
    listStudios(
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        studioTypes
        studioSize
        numberOfPeople
        city
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        pricingMode
        tieredPricing {
          minPersons
          maxPersons
          pricePerHour
        }
        packages {
          id
          name
          durationHours
          prices {
            minPersons
            maxPersons
            price
          }
        }
        tags {
          items {
            tag {
              id
              name
            }
          }
        }
        status
      }
      nextToken
    }
  }
`;
